import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import type { CampaignBuilderMode } from '@/features/campaigns/types/campaign';
import { fetchWalletBalance } from '@/features/dashboard/api/dashboardApi';
import { queryKeys } from '@/shared/lib/query/queryKeys';
import { clearAiCampaignDraft } from '@/features/campaigns/utils/aiCampaignDraftStorage';
import {
  consumeFreshCampaignStart,
  getStoredRepetitionType,
  setStoredRepetitionType,
  type RepetitionType,
} from '@/features/campaigns/utils/startFreshCampaignCreate';
import { useManualAlertOptional } from '@/features/campaigns/components/ManualBuilder/ManualAlert';
import { useAiCampaignState } from './useAiCampaignState';
import { useStandardCampaignWizard } from './useStandardCampaignWizard';

function resolveInitialRepetitionType(
  locationState: unknown,
): RepetitionType {
  if (
    locationState &&
    typeof locationState === 'object' &&
    'repetitionType' in locationState &&
    (locationState as { repetitionType?: unknown }).repetitionType ===
      'screen_wise'
  ) {
    return 'screen_wise';
  }
  return getStoredRepetitionType();
}

export function useCampaignCreatePage() {
  const { mode: routeMode } = useParams<{ mode?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const topRef = useRef<HTMLDivElement>(null);

  const [repetitionType, setRepetitionTypeState] = useState<RepetitionType>(() =>
    resolveInitialRepetitionType(location.state),
  );

  const setRepetitionType = useCallback((next: RepetitionType) => {
    setRepetitionTypeState(next);
    setStoredRepetitionType(next);
  }, []);

  const initialMode =
    routeMode === 'ai' || routeMode === 'standard'
      ? (routeMode as CampaignBuilderMode)
      : null;

  // Consume once at page level so AI + standard hooks don't race.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const freshStart = useMemo(() => {
    const freshId =
      location.state &&
        typeof location.state === 'object' &&
        typeof (location.state as { freshId?: unknown }).freshId === 'number'
        ? (location.state as { freshId: number }).freshId
        : undefined;
    return consumeFreshCampaignStart(freshId);
  }, []);

  const [builderMode, setBuilderMode] = useState<CampaignBuilderMode | null>(
    initialMode,
  );
  const [showSuccess, setShowSuccess] = useState(false);
  const [successCampaignId, setSuccessCampaignId] = useState<string>();
  const [isCampaignCompleted, setIsCampaignCompleted] = useState(false);

  const walletQuery = useQuery({
    queryKey: queryKeys.wallet.balance(),
    queryFn: fetchWalletBalance,
    refetchOnWindowFocus: false,
  });
  const availableBalance = Number(walletQuery.data?.data?.availableBalance ?? 0);

  useEffect(() => {
    if (routeMode === 'ai' || routeMode === 'standard') {
      setBuilderMode(routeMode);
    } else {
      setBuilderMode(null);
    }
  }, [routeMode]);

  const ai = useAiCampaignState({
    routeMode,
    builderMode,
    isCampaignCompleted,
    showSuccess,
    freshStart,
  });

  const onManualLaunched = useCallback((campaignId: string) => {
    setIsCampaignCompleted(true);
    setSuccessCampaignId(campaignId);
    setShowSuccess(true);
  }, []);

  const standard = useStandardCampaignWizard({
    routeMode,
    builderMode,
    isCampaignCompleted,
    showSuccess,
    availableBalance,
    onLaunched: onManualLaunched,
    freshStart,
    repetitionType,
  });

  // Scroll on wizard step change or when AI preview first appears/leaves —
  // not on every aiGeneratedData patch (e.g. coupon re-generate).
  const hasAiPreview = Boolean(ai.aiGeneratedData);
  useEffect(() => {
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [standard.currentStep, hasAiPreview]);

  const { setAiGeneratedData } = ai;
  const { releaseSlotHold } = standard;
  const [timerKey, setTimerKey] = useState(0);

  const manualAlert = useManualAlertOptional();

  const handleSessionExpiry = useCallback(() => {
    if (isCampaignCompleted || showSuccess) return;

    if (builderMode === 'standard') {
      releaseSlotHold();
      setTimerKey((k) => k + 1);
      manualAlert?.warning(
        'Reservation expired',
        'Your slot hold timed out. Select time slots again to continue — campaign details are still saved.',
        { actions: [{ label: 'Book again', primary: true }] },
      );
      return;
    }

    if (builderMode === 'ai') {
      setAiGeneratedData(null);
      setTimerKey((k) => k + 1);
      toast.info(
        'Session expired. Your brief was kept — generate the plan again to continue.',
      );
    }
  }, [
    builderMode,
    isCampaignCompleted,
    manualAlert,
    releaseSlotHold,
    setAiGeneratedData,
    showSuccess,
  ]);

  const onAiLaunchSuccess = (campaignId?: string) => {
    clearAiCampaignDraft();
    setSuccessCampaignId(campaignId);
    setShowSuccess(true);
  };

  const showTimer =
    ((builderMode === 'ai' && Boolean(ai.aiGeneratedData)) ||
      (builderMode === 'standard' && standard.currentStep >= 3)) &&
    !showSuccess &&
    !isCampaignCompleted;

  return {
    topRef,
    builderMode,
    currentStep: standard.currentStep,
    campaignData: standard.campaignData,
    setCampaignData: standard.setCampaignData,
    formData: ai.formData,
    setFormData: ai.setFormData,
    aiGeneratedData: ai.aiGeneratedData,
    setAiGeneratedData: ai.setAiGeneratedData,
    stepTwoData: standard.stepTwoData,
    selectedScreensMeta: standard.selectedScreensMeta,
    submitData: standard.submitData,
    setSubmitData: standard.setSubmitData,
    previewData: standard.previewData,
    creativePreview: standard.creativePreview,
    setCreativePreview: standard.setCreativePreview,
    altMediaIds: standard.altMediaIds,
    setAltMediaIds: standard.setAltMediaIds,
    errors: standard.errors,
    clearError: standard.clearError,
    availableBalance,
    showSuccess,
    setShowSuccess,
    successCampaignId,
    isBusy: standard.isBusy,
    coupon: standard.coupon,
    needsTopUp: standard.needsTopUp,
    belowMinOrder: standard.belowMinOrder,
    exceedsMaxOrder: standard.exceedsMaxOrder,
    launchShortfall: standard.launchShortfall,
    depositOpen: standard.depositOpen,
    depositAmount: standard.depositAmount,
    closeDeposit: standard.closeDeposit,
    handleDepositSuccess: standard.handleDepositSuccess,
    showTimer,
    timerKey,
    handleSessionExpiry,
    handleSelectScreen: standard.handleSelectScreen,
    handleBatchSelectScreens: standard.handleBatchSelectScreens,
    handleClearAllScreens: standard.handleClearAllScreens,
    handleNext: standard.handleNext,
    handleBack: standard.handleBack,
    goToStep: standard.goToStep,
    maxStepReached: standard.maxStepReached,
    skipScreenStep: standard.skipScreenStep,
    slotsSessionKey: standard.slotsSessionKey,
    slotsHoldExpired: standard.slotsHoldExpired,
    screenSelectionSource: standard.screenSelectionSource,
    detectingOrientation: standard.detectingOrientation,
    setDetectingOrientation: standard.setDetectingOrientation,
    repetitionType,
    setRepetitionType,
    resetBookingSlots: standard.resetBookingSlots,
    setIsCampaignCompleted,
    onAiLaunchSuccess,
    navigate,
  };
}
